import type {
  DocumentProjectionDescriptor,
  DocumentSessionBootstrap,
  DocumentSessionClientUpdate,
  DocumentSessionCheckpoint,
  DocumentSessionLease,
  DocumentSessionServerMessage,
  SheetChunkDescriptor,
} from '../protocol/documentSessionProtocol'
import * as Y from 'yjs'
import type { Doc as YDoc } from 'yjs'
import {
  DocumentSessionStore,
  type DocumentSessionPendingUpdateRecordV1,
  type DocumentSessionReplicaCacheRecordV1,
} from './DocumentSessionStore'
import {
  buildDocumentSessionBootstrapMessage,
  buildDocumentSessionCheckpoint,
  buildDocumentSessionResumeMessage,
  buildDocumentSessionSyncMessage,
  normalizeDocumentSessionClientUpdate,
  normalizeDocumentSessionServerMessage,
} from '../protocol/documentSessionProtocol'
import {
  getSpreadsheetOperationDocumentSessionChunkIds,
  groupSpreadsheetOperationsByDocumentSessionChunk,
  type SpreadsheetEngineOperationEnvelope,
  type SpreadsheetWorkbookSnapshot,
} from '../protocol/spreadsheetEngineProtocol'
import type { WorkbookBootstrap } from '../protocol/workbookDocumentProtocol'
import type {
  SpreadsheetExcelIoWarningHistoryEntry,
} from '../protocol/spreadsheetExcelUnsupportedArtifacts'
import { DocumentSessionReplica, type DocumentSessionMaterializedState } from './DocumentSessionReplica'
import { getCloudUnavailableReasonFromError } from './cloudAvailability'

export type WorkbookSessionReplicaSnapshot = {
  workbook_id: string
  session_id: string
  snapshot_seq: number
  snapshot_payload_b64?: string | null
  materialized_root_payload_b64?: string | null
  materialized_chunk_payloads_b64?: Record<string, string>
  head_op_seq: number
  chunk_descriptors: SheetChunkDescriptor[]
  projection_descriptors: DocumentProjectionDescriptor[]
}

export interface WorkbookSessionTransport {
  getWorkbookBootstrap?(workbookId: string, opts?: { timeoutMs?: number }): Promise<WorkbookBootstrap>
  getWorkbookDocumentSessionBootstrap(workbookId: string, opts?: { timeoutMs?: number }): Promise<DocumentSessionBootstrap>
  workbookDocumentSessionWebSocketUrl(workbookId: string): string
  workbookDocumentSessionWebSocketUrlAsync?(workbookId: string): Promise<string>
}

export interface WorkbookSessionPersistence {
  getCheckpoint(workbookId: string): Promise<DocumentSessionCheckpoint | null>
  putCheckpoint(checkpoint: DocumentSessionCheckpoint): Promise<void>
  getReplica(workbookId: string): Promise<DocumentSessionReplicaCacheRecordV1 | null>
  putReplica(replica: {
    workbook_id: string
    session_id: string
    snapshot_seq: number
    snapshot_payload_b64?: string | null
    materialized_root_payload_b64?: string | null
    materialized_chunk_payloads_b64?: Record<string, string>
    head_op_seq: number
    chunk_descriptors: SheetChunkDescriptor[]
    projection_descriptors: DocumentProjectionDescriptor[]
    lease?: DocumentSessionLease | null
  }): Promise<void>
  listPendingUpdates(workbookId: string): Promise<DocumentSessionPendingUpdateRecordV1[]>
  putPendingUpdate(workbookId: string, update: DocumentSessionClientUpdate): Promise<void>
  deletePendingUpdates(workbookId: string, clientUpdateIds: string[]): Promise<void>
}

export type WorkbookSessionSyncState =
  | 'idle'
  | 'local_ready'
  | 'syncing'
  | 'synced'
  | 'cloud_unavailable'

export type WorkbookSessionStatus = {
  state: WorkbookSessionSyncState
  pendingUpdateCount: number
  lastError: string | null
  updatedAtMs: number
}

function getOrCreateDocumentSessionClientId(): string {
  if (typeof window === 'undefined') return 'server'
  const key = 'fusegrid_document_session_client_id'
  const existing = String(window.localStorage.getItem(key) || '').trim()
  if (existing) return existing
  const next = `docsess_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`
  try { window.localStorage.setItem(key, next) } catch { }
  return next
}

function newDocumentSessionUpdateId(): string {
  return `upd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`
}

const LEGACY_MIRROR_PENDING_UPDATE_B64_BYTES = 1_000_000

type DocumentSessionLogEntry = {
  event: string
  ts: number
} & Record<string, unknown>

declare global {
  interface Window {
    __FG_DOCUMENT_SESSION_LOGS__?: DocumentSessionLogEntry[]
    __FG_DOCUMENT_SESSION_LAST__?: DocumentSessionLogEntry
    __FG_DOCUMENT_SESSION_STATUS__?: WorkbookSessionStatus
  }
}

function sessionPerfNow(): number {
  return (
    typeof performance !== 'undefined' &&
    typeof performance.now === 'function'
  )
    ? performance.now()
    : Date.now()
}

function logDocumentSessionEvent(event: string, data?: Record<string, unknown>): void {
  const payload: DocumentSessionLogEntry = {
    event,
    ts: Date.now(),
    ...(data || {}),
  }
  try {
    console.log('[FG-DOC-SESSION]', payload)
  } catch { }
  if (typeof window === 'undefined') return
  try {
    const entries = Array.isArray(window.__FG_DOCUMENT_SESSION_LOGS__)
      ? window.__FG_DOCUMENT_SESSION_LOGS__
      : []
    entries.push(payload)
    while (entries.length > 200) entries.shift()
    window.__FG_DOCUMENT_SESSION_LOGS__ = entries
    window.__FG_DOCUMENT_SESSION_LAST__ = payload
  } catch { }
}

function decodeBase64ToBytes(payloadB64: string): Uint8Array | null {
  const payload = String(payloadB64 || '').trim()
  if (!payload) return null
  try {
    if (typeof Buffer !== 'undefined') {
      return Uint8Array.from(Buffer.from(payload, 'base64'))
    }
  } catch { }
  try {
    if (typeof atob === 'function') {
      const raw = atob(payload)
      const out = new Uint8Array(raw.length)
      for (let index = 0; index < raw.length; index += 1) {
        out[index] = raw.charCodeAt(index)
      }
      return out
    }
  } catch { }
  return null
}

function isWorkbookSessionRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function shouldDiscardLegacyMirrorPendingUpdate(
  update: unknown,
): boolean {
  if (!isWorkbookSessionRecord(update)) return false
  if (String(update.doc_scope || '').trim() !== 'workbook_root') return false
  const payloadB64 = String(update.update_payload_b64 || '').trim()
  if (payloadB64.length < LEGACY_MIRROR_PENDING_UPDATE_B64_BYTES) return false
  const bytes = decodeBase64ToBytes(payloadB64)
  if (!bytes) return false
  try {
    const doc = new Y.Doc()
    Y.applyUpdate(doc, bytes)
    const restoreSeedJson = String(doc.getMap('cloud_session').get('restore_seed_json') || '').trim()
    return restoreSeedJson.length > 0
  } catch {
    return false
  }
}

export class WorkbookSessionFacade {
  private readonly transport: WorkbookSessionTransport
  private readonly workbookId: string
  private readonly store: WorkbookSessionPersistence
  private readonly clientId: string
  private ws: WebSocket | null = null
  private checkpoint: DocumentSessionCheckpoint | null = null
  private lease: DocumentSessionLease | null = null
  private replica: WorkbookSessionReplicaSnapshot | null = null
  private readonly replicaListeners = new Set<(snapshot: WorkbookSessionReplicaSnapshot | null) => void>()
  private readonly statusListeners = new Set<(status: WorkbookSessionStatus) => void>()
  private status: WorkbookSessionStatus = {
    state: 'idle',
    pendingUpdateCount: 0,
    lastError: null,
    updatedAtMs: Date.now(),
  }
  private readonly documentReplica = new DocumentSessionReplica()
  private readonly pendingUpdates: DocumentSessionClientUpdate[] = []
  private sessionReady = false
  private bootstrapPromise: Promise<WorkbookSessionReplicaSnapshot> | null = null
  private lastWorkbookBootstrap: WorkbookBootstrap | null = null

  constructor(
    transport: WorkbookSessionTransport,
    workbookIdRaw: string,
    store: WorkbookSessionPersistence = new DocumentSessionStore(),
  ) {
    this.transport = transport
    this.workbookId = String(workbookIdRaw || '').trim()
    this.store = store
    this.clientId = getOrCreateDocumentSessionClientId()
  }

  onReplicaChange(cb: ((snapshot: WorkbookSessionReplicaSnapshot | null) => void) | null): void {
    this.replicaListeners.clear()
    if (cb) this.subscribeReplicaChange(cb)
  }

  subscribeReplicaChange(cb: (snapshot: WorkbookSessionReplicaSnapshot | null) => void): () => void {
    this.replicaListeners.add(cb)
    try { cb(this.replica) } catch { }
    return () => {
      this.replicaListeners.delete(cb)
    }
  }

  onStatusChange(cb: ((status: WorkbookSessionStatus) => void) | null): void {
    this.statusListeners.clear()
    if (cb) this.subscribeStatusChange(cb)
  }

  subscribeStatusChange(cb: (status: WorkbookSessionStatus) => void): () => void {
    this.statusListeners.add(cb)
    try { cb(this.status) } catch { }
    return () => {
      this.statusListeners.delete(cb)
    }
  }

  getStatus(): WorkbookSessionStatus {
    return this.status
  }

  getReplicaSnapshot(): WorkbookSessionReplicaSnapshot | null {
    return this.replica
  }

  getMaterializedReplicaState(): DocumentSessionMaterializedState {
    return this.documentReplica.getMaterializedState()
  }

  getLastWorkbookBootstrap(): WorkbookBootstrap | null {
    return this.lastWorkbookBootstrap
  }

  readRootMap(mapName: string): Record<string, unknown> | null {
    return this.documentReplica.readRootMap(mapName)
  }

  submitWorkbookRootChange(mutate: (doc: YDoc) => void): string | null {
    const updatePayloadB64 = this.documentReplica.buildWorkbookRootUpdate(mutate)
    return this.submitLocalReplicaUpdate('workbook_root', null, updatePayloadB64)
  }

  submitSheetChunkChange(chunkIdRaw: string, mutate: (doc: YDoc) => void): string | null {
    const chunkId = String(chunkIdRaw || '').trim()
    if (!chunkId) return null
    const updatePayloadB64 = this.documentReplica.buildSheetChunkUpdate(chunkId, mutate)
    return this.submitLocalReplicaUpdate('sheet_chunk', chunkId, updatePayloadB64)
  }

  submitSpreadsheetEngineOperations(operations: SpreadsheetEngineOperationEnvelope[]): string[] {
    const rootOps = (Array.isArray(operations) ? operations : [])
      .filter((operation) => getSpreadsheetOperationDocumentSessionChunkIds(operation).length === 0)
    const grouped = groupSpreadsheetOperationsByDocumentSessionChunk(operations)
    const clientUpdateIds: string[] = []
    if (rootOps.length > 0) {
      const updatePayloadB64 = this.documentReplica.appendWorkbookRootSpreadsheetEngineOps(rootOps)
      const clientUpdateId = this.submitLocalReplicaUpdate('workbook_root', null, updatePayloadB64)
      if (clientUpdateId) clientUpdateIds.push(clientUpdateId)
    }
    for (const [chunkId, chunkOps] of grouped.entries()) {
      const updatePayloadB64 = this.documentReplica.appendSheetChunkSpreadsheetEngineOps(chunkId, chunkOps)
      const clientUpdateId = this.submitLocalReplicaUpdate('sheet_chunk', chunkId, updatePayloadB64)
      if (clientUpdateId) clientUpdateIds.push(clientUpdateId)
    }
    return clientUpdateIds
  }

  submitSpreadsheetWorkbookSnapshot(snapshot: SpreadsheetWorkbookSnapshot): string | null {
    const discardedPendingUpdateIds = this.pendingUpdates
      .map((update) => String(update.client_update_id || '').trim())
      .filter(Boolean)
    this.pendingUpdates.splice(0, this.pendingUpdates.length)
    if (discardedPendingUpdateIds.length > 0) {
      void this.store.deletePendingUpdates(this.workbookId, discardedPendingUpdateIds).catch(() => null)
      logDocumentSessionEvent('pending_updates_pruned', {
        workbook_id: this.workbookId,
        discarded_count: discardedPendingUpdateIds.length,
        reason: 'spreadsheet_workbook_snapshot_replace',
      })
    }
    const updatePayloadB64 = this.documentReplica.replaceSpreadsheetWorkbookSnapshot(snapshot)
    return this.submitLocalReplicaUpdate('workbook_root', null, updatePayloadB64)
  }

  submitSpreadsheetExcelIoWarningHistoryEntry(
    entry: SpreadsheetExcelIoWarningHistoryEntry | null | undefined,
  ): string | null {
    const updatePayloadB64 = this.documentReplica.appendSpreadsheetExcelIoWarningHistoryEntry(entry)
    return this.submitLocalReplicaUpdate('workbook_root', null, updatePayloadB64)
  }

  readSpreadsheetWorkbookSnapshot(): SpreadsheetWorkbookSnapshot | null {
    return this.documentReplica.readSpreadsheetWorkbookSnapshot()
  }

  readSpreadsheetExcelIoWarningHistory(): SpreadsheetExcelIoWarningHistoryEntry[] {
    return this.documentReplica.readSpreadsheetExcelIoWarningHistory()
  }

  readSpreadsheetWorkbookSnapshotReplacementMs(): number {
    return this.documentReplica.readSpreadsheetWorkbookSnapshotReplacementMs()
  }

  readSpreadsheetEngineOperations(): SpreadsheetEngineOperationEnvelope[] {
    return this.documentReplica.readSpreadsheetEngineOperations()
  }

  async bootstrap(): Promise<WorkbookSessionReplicaSnapshot> {
    if (this.replica) return this.replica
    if (this.bootstrapPromise) return await this.bootstrapPromise
    const promise = this.bootstrapLocalOrFresh()
    this.bootstrapPromise = promise
    try {
      return await promise
    } finally {
      if (this.bootstrapPromise === promise) {
        this.bootstrapPromise = null
      }
    }
  }

  private async bootstrapLocalOrFresh(): Promise<WorkbookSessionReplicaSnapshot> {
    const localReplica = await this.hydrateFromLocalStore().catch(() => null)
    if (localReplica) return localReplica
    return await this.bootstrapFresh({ skipLocalHydrate: true, localReplica })
  }

  async bootstrapAuthoritative(opts?: { timeoutMs?: number }): Promise<WorkbookSessionReplicaSnapshot> {
    const startedAt = sessionPerfNow()
    if (!this.workbookId) throw new Error('Missing workbook id')
    await this.hydratePendingUpdatesFromLocalStore()
    this.setStatus('syncing')
    try {
      const bootstrap = await this.readAuthoritativeDocumentSessionBootstrap(opts)
      const replica = this.applyBootstrap(bootstrap)
      this.setStatus(this.pendingUpdates.length > 0 ? 'syncing' : 'synced')
      logDocumentSessionEvent('authoritative_bootstrap_done', {
        workbook_id: this.workbookId,
        elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
        snapshot_seq: Number(bootstrap.snapshot_seq || 0),
        tail_count: Array.isArray(bootstrap.tail) ? bootstrap.tail.length : 0,
        chunk_descriptor_count: Array.isArray(bootstrap.chunk_descriptors) ? bootstrap.chunk_descriptors.length : 0,
        projection_descriptor_count: Array.isArray(bootstrap.projection_descriptors) ? bootstrap.projection_descriptors.length : 0,
        has_root_snapshot: Boolean(bootstrap.snapshot_payload_b64),
      })
      return replica
    } catch (error) {
      this.setStatus('cloud_unavailable', error)
      logDocumentSessionEvent('authoritative_bootstrap_failed', {
        workbook_id: this.workbookId,
        elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
        pending_update_count: this.pendingUpdates.length,
        error: String((error as any)?.message || error || ''),
      })
      throw error
    }
  }

  private async readAuthoritativeDocumentSessionBootstrap(
    opts?: { timeoutMs?: number },
  ): Promise<DocumentSessionBootstrap> {
    if (typeof this.transport.getWorkbookBootstrap === 'function') {
      const unifiedBootstrap = await this.transport.getWorkbookBootstrap(this.workbookId, opts)
      this.lastWorkbookBootstrap = unifiedBootstrap
      logDocumentSessionEvent('workbook_bootstrap_done', {
        workbook_id: this.workbookId,
        manifest_source: unifiedBootstrap.manifest_source,
        manifest_version: Number(unifiedBootstrap.manifest?.version || 0),
        active_tab_kind: String(unifiedBootstrap.active_tab?.kind || '').trim() || null,
        active_tab_name: String(unifiedBootstrap.active_tab?.name || '').trim() || null,
        projection_status_count: Array.isArray(unifiedBootstrap.projection_statuses)
          ? unifiedBootstrap.projection_statuses.length
          : 0,
      })
      return unifiedBootstrap.document_session
    }
    this.lastWorkbookBootstrap = null
    return await this.transport.getWorkbookDocumentSessionBootstrap(this.workbookId, opts)
  }

  private async bootstrapFresh(opts?: {
    skipLocalHydrate?: boolean
    localReplica?: WorkbookSessionReplicaSnapshot | null
  }): Promise<WorkbookSessionReplicaSnapshot> {
    const startedAt = sessionPerfNow()
    const localReplica = opts?.skipLocalHydrate
      ? (opts.localReplica ?? null)
      : await this.hydrateFromLocalStore().catch(() => null)
    this.setStatus(localReplica ? 'local_ready' : 'syncing')
    try {
      const bootstrap = await this.readAuthoritativeDocumentSessionBootstrap()
      const replica = this.applyBootstrap(bootstrap)
      this.setStatus(this.pendingUpdates.length > 0 ? 'syncing' : 'synced')
      logDocumentSessionEvent('bootstrap_done', {
        workbook_id: this.workbookId,
        elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
        snapshot_seq: Number(bootstrap.snapshot_seq || 0),
        tail_count: Array.isArray(bootstrap.tail) ? bootstrap.tail.length : 0,
        chunk_descriptor_count: Array.isArray(bootstrap.chunk_descriptors) ? bootstrap.chunk_descriptors.length : 0,
        projection_descriptor_count: Array.isArray(bootstrap.projection_descriptors) ? bootstrap.projection_descriptors.length : 0,
        has_root_snapshot: Boolean(bootstrap.snapshot_payload_b64),
      })
      return replica
    } catch (error) {
      if (!this.isRecoverableBootstrapError(error)) {
        this.setStatus('cloud_unavailable', error)
        throw error
      }
      const fallback = localReplica || this.ensureLocalReplica('bootstrap_failed')
      this.setStatus('cloud_unavailable', error)
      logDocumentSessionEvent('bootstrap_local_fallback', {
        workbook_id: this.workbookId,
        elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
        pending_update_count: this.pendingUpdates.length,
        error: String((error as any)?.message || error || ''),
      })
      if (fallback) return fallback
      throw error
    }
  }

  async hydrateFromLocalStore(): Promise<WorkbookSessionReplicaSnapshot | null> {
    const startedAt = sessionPerfNow()
    if (!this.workbookId) return null
    const [checkpoint, replica, pendingUpdates] = await Promise.all([
      this.store.getCheckpoint(this.workbookId).catch(() => null),
      this.store.getReplica(this.workbookId).catch(() => null),
      this.store.listPendingUpdates(this.workbookId).catch(() => []),
    ])
    if (checkpoint) this.checkpoint = checkpoint
    const pendingRecords: unknown[] = Array.isArray(pendingUpdates) ? pendingUpdates : []
    const discardedPendingUpdateIds = pendingRecords
      .filter((entry) => shouldDiscardLegacyMirrorPendingUpdate(entry))
      .map((entry) => isWorkbookSessionRecord(entry) ? String(entry.client_update_id || '').trim() : '')
      .filter(Boolean)
    const discardedPendingUpdateIdSet = new Set(discardedPendingUpdateIds)
    const keptPendingRecords = pendingRecords
      .map((entry) => normalizeDocumentSessionClientUpdate(entry))
      .filter((entry): entry is DocumentSessionClientUpdate => Boolean(entry))
      .filter((entry) => !discardedPendingUpdateIdSet.has(entry.client_update_id))
    this.pendingUpdates.splice(0, this.pendingUpdates.length, ...keptPendingRecords)
    if (discardedPendingUpdateIds.length > 0) {
      void this.store.deletePendingUpdates(this.workbookId, discardedPendingUpdateIds).catch(() => null)
      logDocumentSessionEvent('pending_updates_pruned', {
        workbook_id: this.workbookId,
        discarded_count: discardedPendingUpdateIds.length,
        reason: 'legacy_restore_seed_payload',
      })
    }
    if (!replica) {
      logDocumentSessionEvent('local_hydrate_done', {
        workbook_id: this.workbookId,
        elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
        found_replica: false,
        found_checkpoint: Boolean(checkpoint),
        pending_update_count: this.pendingUpdates.length,
      })
      this.setStatus(this.pendingUpdates.length > 0 ? 'local_ready' : 'idle')
      return this.replica
    }
    this.lease = replica.lease ?? this.lease
    let materialized = this.documentReplica.hydrateFromPersistedState({
      root_payload_b64: replica.materialized_root_payload_b64 ?? replica.snapshot_payload_b64 ?? null,
      chunk_payloads_b64: replica.materialized_chunk_payloads_b64 ?? {},
    })
    if (this.pendingUpdates.length > 0) {
      materialized = this.reapplyPendingUpdatesToReplica()
    }
    this.replica = {
      workbook_id: replica.workbook_id,
      session_id: replica.session_id,
      snapshot_seq: replica.snapshot_seq,
      snapshot_payload_b64: replica.snapshot_payload_b64 ?? null,
      materialized_root_payload_b64: materialized.root_payload_b64,
      materialized_chunk_payloads_b64: materialized.chunk_payloads_b64,
      head_op_seq: replica.head_op_seq,
      chunk_descriptors: Array.isArray(replica.chunk_descriptors) ? replica.chunk_descriptors : [],
      projection_descriptors: Array.isArray(replica.projection_descriptors) ? replica.projection_descriptors : [],
    }
    this.emitReplicaChange()
    logDocumentSessionEvent('local_hydrate_done', {
      workbook_id: this.workbookId,
      elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
      found_replica: true,
      found_checkpoint: Boolean(checkpoint),
      pending_update_count: this.pendingUpdates.length,
      snapshot_seq: Number(replica.snapshot_seq || 0),
      head_op_seq: Number(replica.head_op_seq || 0),
    })
    this.setStatus(this.pendingUpdates.length > 0 ? 'local_ready' : 'synced')
    return this.replica
  }

  private async hydratePendingUpdatesFromLocalStore(): Promise<void> {
    if (!this.workbookId) return
    const pendingUpdates = await this.store.listPendingUpdates(this.workbookId).catch(() => [])
    const pendingRecords: unknown[] = Array.isArray(pendingUpdates) ? pendingUpdates : []
    const discardedPendingUpdateIds = pendingRecords
      .filter((entry) => shouldDiscardLegacyMirrorPendingUpdate(entry))
      .map((entry) => isWorkbookSessionRecord(entry) ? String(entry.client_update_id || '').trim() : '')
      .filter(Boolean)
    const discardedPendingUpdateIdSet = new Set(discardedPendingUpdateIds)
    const keptPendingRecords = pendingRecords
      .map((entry) => normalizeDocumentSessionClientUpdate(entry))
      .filter((entry): entry is DocumentSessionClientUpdate => Boolean(entry))
      .filter((entry) => !discardedPendingUpdateIdSet.has(entry.client_update_id))
    this.pendingUpdates.splice(0, this.pendingUpdates.length, ...keptPendingRecords)
    if (discardedPendingUpdateIds.length > 0) {
      void this.store.deletePendingUpdates(this.workbookId, discardedPendingUpdateIds).catch(() => null)
      logDocumentSessionEvent('pending_updates_pruned', {
        workbook_id: this.workbookId,
        discarded_count: discardedPendingUpdateIds.length,
        reason: 'legacy_restore_seed_payload',
      })
    }
  }

  private applyBootstrap(bootstrap: DocumentSessionBootstrap): WorkbookSessionReplicaSnapshot {
    let materialized = this.documentReplica.hydrateFromBootstrap(bootstrap)
    if (this.pendingUpdates.length > 0) {
      materialized = this.reapplyPendingUpdatesToReplica()
    }
    const nextReplica: WorkbookSessionReplicaSnapshot = {
      workbook_id: bootstrap.workbook_id,
      session_id: bootstrap.session_id,
      snapshot_seq: Number(bootstrap.snapshot_seq || 0),
      snapshot_payload_b64: bootstrap.snapshot_payload_b64 ? String(bootstrap.snapshot_payload_b64) : null,
      materialized_root_payload_b64: materialized.root_payload_b64,
      materialized_chunk_payloads_b64: materialized.chunk_payloads_b64,
      head_op_seq: Math.max(
        Number(bootstrap.snapshot_seq || 0),
        ...((Array.isArray(bootstrap.tail) ? bootstrap.tail : []).map((entry) => Number(entry?.op_seq || 0))),
      ),
      chunk_descriptors: Array.isArray(bootstrap.chunk_descriptors) ? bootstrap.chunk_descriptors : [],
      projection_descriptors: Array.isArray(bootstrap.projection_descriptors) ? bootstrap.projection_descriptors : [],
    }
    this.lease = bootstrap.lease
    this.checkpoint = buildDocumentSessionCheckpoint(
      bootstrap.workbook_id,
      bootstrap.session_id,
      bootstrap.snapshot_seq,
      nextReplica.head_op_seq,
    )
    this.replica = nextReplica
    void this.store.putCheckpoint(this.checkpoint).catch(() => null)
    void this.store.putReplica({
      ...nextReplica,
      lease: bootstrap.lease ?? null,
    }).catch(() => null)
    this.emitReplicaChange()
    return nextReplica
  }

  private reapplyPendingUpdatesToReplica(): DocumentSessionMaterializedState {
    const baseSeq = Math.max(0, Number(this.replica?.head_op_seq || this.checkpoint?.last_op_seq || 0))
    return this.documentReplica.applyCommittedUpdates(this.pendingUpdates.map((update, index) => ({
      op_seq: baseSeq + index + 1,
      doc_scope: update.doc_scope,
      chunk_id: update.chunk_id ?? null,
      update_payload_b64: update.update_payload_b64,
      actor_user_id: this.clientId,
      client_id: update.client_id,
      client_update_id: update.client_update_id,
    })))
  }

  private ensureLocalReplica(reason: string): WorkbookSessionReplicaSnapshot | null {
    if (!this.workbookId) return this.replica
    if (this.replica) {
      if (!this.checkpoint) {
        this.checkpoint = buildDocumentSessionCheckpoint(
          this.replica.workbook_id,
          this.replica.session_id,
          this.replica.snapshot_seq,
          this.replica.head_op_seq,
        )
        void this.store.putCheckpoint(this.checkpoint).catch(() => null)
      }
      return this.replica
    }
    const materialized = this.documentReplica.getMaterializedState()
    const sessionId = String(this.checkpoint?.session_id || this.lease?.session_id || this.workbookId).trim()
      || this.workbookId
    const snapshotSeq = Math.max(0, Number(this.checkpoint?.snapshot_seq || 0))
    const headOpSeq = Math.max(snapshotSeq, Number(this.checkpoint?.last_op_seq || 0))
    this.lease = this.lease ?? {
      session_id: sessionId,
      resume_token: `resume:${this.workbookId}`,
      expires_at_epoch_ms: Date.now() + 60_000,
    }
    this.checkpoint = this.checkpoint ?? buildDocumentSessionCheckpoint(
      this.workbookId,
      sessionId,
      snapshotSeq,
      headOpSeq,
    )
    this.replica = {
      workbook_id: this.workbookId,
      session_id: sessionId,
      snapshot_seq: snapshotSeq,
      snapshot_payload_b64: null,
      materialized_root_payload_b64: materialized.root_payload_b64,
      materialized_chunk_payloads_b64: materialized.chunk_payloads_b64,
      head_op_seq: headOpSeq,
      chunk_descriptors: [],
      projection_descriptors: [],
    }
    void this.store.putCheckpoint(this.checkpoint).catch(() => null)
    void this.store.putReplica({
      ...this.replica,
      lease: this.lease,
    }).catch(() => null)
    this.emitReplicaChange()
    logDocumentSessionEvent('local_replica_created', {
      workbook_id: this.workbookId,
      session_id: sessionId,
      reason,
      pending_update_count: this.pendingUpdates.length,
    })
    return this.replica
  }

  private isRecoverableBootstrapError(error: unknown): boolean {
    if (getCloudUnavailableReasonFromError(error) !== null) return true
    const status = Number((error as any)?.status ?? NaN)
    if (Number.isFinite(status) && status >= 500) return true
    const text = String((error as any)?.message || error || '').toLowerCase()
    return (
      text.includes('failed to fetch') ||
      text.includes('networkerror') ||
      text.includes('econnrefused') ||
      text.includes('timeout') ||
      text.includes('timed out')
    )
  }

  private setStatus(state: WorkbookSessionSyncState, error?: unknown): void {
    const lastError = error == null ? this.status.lastError : String((error as any)?.message || error || '')
    this.status = {
      state,
      pendingUpdateCount: this.pendingUpdates.length,
      lastError: state === 'cloud_unavailable' ? lastError : null,
      updatedAtMs: Date.now(),
    }
    if (typeof window !== 'undefined') {
      try {
        window.__FG_DOCUMENT_SESSION_STATUS__ = this.status
      } catch { }
    }
    for (const cb of [...this.statusListeners]) {
      try { cb(this.status) } catch { }
    }
    logDocumentSessionEvent('status', {
      workbook_id: this.workbookId,
      state,
      pending_update_count: this.pendingUpdates.length,
      last_error: this.status.lastError,
    })
  }

  private emitReplicaChange(): void {
    for (const cb of [...this.replicaListeners]) {
      try { cb(this.replica) } catch { }
    }
  }

  private isActiveSessionMessage(sessionIdRaw: string): boolean {
    const sessionId = String(sessionIdRaw || '').trim()
    const activeSessionId = String(this.replica?.session_id || this.checkpoint?.session_id || this.lease?.session_id || '').trim()
    return Boolean(sessionId && activeSessionId && sessionId === activeSessionId)
  }

  connect(): void {
    void this.connectInternal()
  }

  private async connectInternal(): Promise<void> {
    const startedAt = sessionPerfNow()
    if (!this.workbookId) return
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return
    if (!this.replica || !this.checkpoint) {
      await this.hydrateFromLocalStore().catch(() => null)
    }
    let url = ''
    try {
      const asyncUrlFactory = this.transport.workbookDocumentSessionWebSocketUrlAsync
      url = typeof asyncUrlFactory === 'function'
        ? await asyncUrlFactory.call(this.transport, this.workbookId)
        : this.transport.workbookDocumentSessionWebSocketUrl(this.workbookId)
    } catch {
      return
    }
    logDocumentSessionEvent('connect_begin', {
      workbook_id: this.workbookId,
      elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
      has_checkpoint: Boolean(this.checkpoint),
      has_lease: Boolean(this.lease),
      has_replica: Boolean(this.replica),
      pending_update_count: this.pendingUpdates.length,
    })
    const ws = new WebSocket(url)
    this.ws = ws
    this.sessionReady = false
    ws.onopen = () => {
      if (this.ws !== ws) return
      this.setStatus(this.pendingUpdates.length > 0 ? 'syncing' : 'synced')
      logDocumentSessionEvent('ws_open', {
        workbook_id: this.workbookId,
        elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
        mode: (this.checkpoint && this.lease) ? 'resume' : 'bootstrap',
      })
      if (this.checkpoint && this.lease) {
        ws.send(JSON.stringify(buildDocumentSessionResumeMessage(this.workbookId, this.checkpoint, this.lease)))
      } else {
        ws.send(JSON.stringify(buildDocumentSessionBootstrapMessage(this.workbookId)))
      }
    }
    ws.onmessage = (event) => {
      void this.handleServerMessage(String(event.data || ''))
    }
    ws.onclose = () => {
      if (this.ws === ws) {
        this.ws = null
        this.sessionReady = false
      }
      logDocumentSessionEvent('ws_close', {
        workbook_id: this.workbookId,
        elapsed_ms: Math.round(Math.max(0, sessionPerfNow() - startedAt)),
        pending_update_count: this.pendingUpdates.length,
      })
      if (this.pendingUpdates.length > 0) {
        this.setStatus('cloud_unavailable', 'Document-session websocket closed with pending updates')
      }
    }
  }

  disconnect(): void {
    const ws = this.ws
    this.ws = null
    try { ws?.close() } catch { }
  }

  private async handleServerMessage(raw: string): Promise<void> {
    let parsed: DocumentSessionServerMessage | null = null
    try {
      parsed = normalizeDocumentSessionServerMessage(JSON.parse(raw))
    } catch {
      return
    }
    if (!parsed) return
    if (String(parsed.workbook_id || '').trim() !== this.workbookId) return
    if (parsed.type === 'bootstrap_ready') {
      this.sessionReady = true
      this.applyBootstrap(parsed)
      logDocumentSessionEvent('bootstrap_ready', {
        workbook_id: parsed.workbook_id,
        snapshot_seq: Number(parsed.snapshot_seq || 0),
        tail_count: Array.isArray(parsed.tail) ? parsed.tail.length : 0,
      })
      this.flushPendingUpdates()
      return
    }
    if (parsed.type === 'resume_ack') {
      if (!this.isActiveSessionMessage(parsed.session_id)) return
      this.sessionReady = true
      this.checkpoint = buildDocumentSessionCheckpoint(
        parsed.workbook_id,
        parsed.session_id,
        parsed.snapshot_seq,
        parsed.head_op_seq,
      )
      if (this.replica) {
        this.replica = {
          ...this.replica,
          head_op_seq: Math.max(this.replica.head_op_seq, parsed.head_op_seq),
        }
        void this.store.putCheckpoint(this.checkpoint).catch(() => null)
        void this.store.putReplica({
          ...this.replica,
          lease: this.lease,
        }).catch(() => null)
        this.emitReplicaChange()
      }
      logDocumentSessionEvent('resume_ack', {
        workbook_id: parsed.workbook_id,
        snapshot_seq: Number(parsed.snapshot_seq || 0),
        head_op_seq: Number(parsed.head_op_seq || 0),
      })
      this.flushPendingUpdates()
      return
    }
    if (parsed.type === 'updates_committed') {
      if (!this.isActiveSessionMessage(parsed.session_id)) return
      const materialized = this.documentReplica.applyCommittedUpdates(parsed.updates)
      const ackedKeys = new Set(
        parsed.updates
          .map((update) => {
            const clientId = String(update?.client_id || '').trim()
            const clientUpdateId = String(update?.client_update_id || '').trim()
            return clientId && clientUpdateId ? `${clientId}::${clientUpdateId}` : ''
          })
          .filter(Boolean),
      )
      if (ackedKeys.size > 0) {
        const ackedUpdateIds: string[] = []
        for (let index = this.pendingUpdates.length - 1; index >= 0; index -= 1) {
          const pending = this.pendingUpdates[index]
          const key = `${String(pending?.client_id || '').trim()}::${String(pending?.client_update_id || '').trim()}`
          if (!ackedKeys.has(key)) continue
          ackedUpdateIds.push(String(pending?.client_update_id || '').trim())
          this.pendingUpdates.splice(index, 1)
        }
        if (ackedUpdateIds.length > 0) {
          void this.store.deletePendingUpdates(this.workbookId, ackedUpdateIds).catch(() => null)
        }
      }
      const headOpSeq = Math.max(
        ...(parsed.updates.map((update) => Number(update?.op_seq || 0))),
        0,
      )
      if (this.replica) {
        this.replica = {
          ...this.replica,
          materialized_root_payload_b64: materialized.root_payload_b64,
          materialized_chunk_payloads_b64: materialized.chunk_payloads_b64,
          head_op_seq: Math.max(this.replica.head_op_seq, headOpSeq),
        }
        this.checkpoint = buildDocumentSessionCheckpoint(
          this.replica.workbook_id,
          this.replica.session_id,
          this.replica.snapshot_seq,
          this.replica.head_op_seq,
        )
        void this.store.putCheckpoint(this.checkpoint).catch(() => null)
        void this.store.putReplica({
          ...this.replica,
          lease: this.lease,
        }).catch(() => null)
        this.emitReplicaChange()
      }
      logDocumentSessionEvent('updates_committed', {
        workbook_id: parsed.workbook_id,
        update_count: parsed.updates.length,
        acked_pending_count: ackedKeys.size,
        head_op_seq: headOpSeq,
      })
      this.setStatus(this.pendingUpdates.length > 0 ? 'syncing' : 'synced')
      return
    }
    if (parsed.type === 'rebootstrap_required') {
      this.sessionReady = false
      logDocumentSessionEvent('rebootstrap_required', {
        workbook_id: parsed.workbook_id,
        reason: parsed.reason,
      })
      await this.bootstrap()
    }
  }

  private submitLocalReplicaUpdate(
    docScope: 'workbook_root' | 'sheet_chunk',
    chunkId: string | null,
    updatePayloadB64: string | null,
  ): string | null {
    const payload = String(updatePayloadB64 || '').trim()
    if (!payload) return null
    const ensuredReplica = this.ensureLocalReplica(`submit_${docScope}`)
    if (!ensuredReplica) return null
    const clientUpdateId = newDocumentSessionUpdateId()
    const materialized = this.documentReplica.getMaterializedState()
    this.replica = {
      ...ensuredReplica,
      materialized_root_payload_b64: materialized.root_payload_b64,
      materialized_chunk_payloads_b64: materialized.chunk_payloads_b64,
    }
    if (this.checkpoint) void this.store.putCheckpoint(this.checkpoint).catch(() => null)
    void this.store.putReplica({
      ...this.replica,
      lease: this.lease,
    }).catch(() => null)
    this.emitReplicaChange()
    this.pendingUpdates.push({
      client_id: this.clientId,
      client_update_id: clientUpdateId,
      doc_scope: docScope,
      chunk_id: chunkId,
      update_payload_b64: payload,
    })
    void this.store.putPendingUpdate(this.workbookId, this.pendingUpdates[this.pendingUpdates.length - 1]).catch(() => null)
    this.setStatus(this.sessionReady ? 'syncing' : 'local_ready')
    this.flushPendingUpdates()
    return clientUpdateId
  }

  private flushPendingUpdates(): void {
    const ws = this.ws
    if (!ws || ws.readyState !== WebSocket.OPEN || !this.sessionReady || !this.pendingUpdates.length) return
    try {
      this.setStatus('syncing')
      ws.send(JSON.stringify(buildDocumentSessionSyncMessage(this.workbookId, [...this.pendingUpdates])))
    } catch {
      this.setStatus('cloud_unavailable', 'Failed to send document-session pending updates')
      // Leave updates queued for the next healthy websocket turn.
    }
  }
}
